import { useState } from 'react';
import axiosInstance from '../axiosInstance';
import { endpoints } from '../endPoints';
import { toast } from 'react-hot-toast';
import useToggle from '../../hooks/use-toggle';

const useAccountantService = () => {
  const [accountants, setAccountants] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, toggleLoading] = useToggle();

  // Create new accountant record
  const createAccountantData = async (accountantDto, onSuccess) => {
    try {
      const exists = await checkAccountantDataExists(accountantDto.termId, accountantDto.studentId);
      
      if (exists) {
        const response = await updateAccountantDataByTermStudent(
          accountantDto.termId, 
          accountantDto.studentId, 
          accountantDto, 
          onSuccess
        );
        return response;
      } else {
        const response = await axiosInstance.post(endpoints.accountants.create, accountantDto);
        toast.success(response.data.message || 'Accountant record created successfully');
        if (onSuccess) {
          onSuccess();
        }
        return response.data;
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating/updating accountant record');
      console.error('Error creating/updating accountant record:', error);
      throw error;
    }
  };

  // Create accountant record by term and student
  const createAccountantDataByTermStudent = async (accountantDto, onSuccess) => {
    try {
      const response = await axiosInstance.post(endpoints.accountants.createByTermStudent, accountantDto);
      toast.success(response.data.message || 'Accountant record created successfully');
      if (onSuccess) {
        onSuccess();
      }
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating accountant record');
      console.error('Error creating accountant record:', error);
      throw error;
    }
  };

  // Update accountant record
  const updateAccountantData = async (accountantDto, onSuccess) => {
    try {
      toggleLoading();
      const response = await axiosInstance.put(endpoints.accountants.update, accountantDto);
      toast.success(response.data.message || 'Accountant record updated successfully');
      if (onSuccess) {
        onSuccess();
      }
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating accountant record');
      console.error('Error updating accountant record:', error);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  // Update accountant record by term and student
  const updateAccountantDataByTermStudent = async (termId, studentId, accountantDto, onSuccess) => {
    try {
      toggleLoading();
      const response = await axiosInstance.put(
        `${endpoints.accountants.updateByTermStudent}/${termId}/${studentId}`,
        accountantDto
      );
      toast.success(response.data.message || 'Accountant record updated successfully');
      if (onSuccess) {
        onSuccess();
      }
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating accountant record');
      console.error('Error updating accountant record:', error);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  // Update accountant record with partial data
  const updatePartialAccountantDataByTermStudent = async (termId, studentId, partialDto, onSuccess) => {
    try {
      toggleLoading();
      const response = await axiosInstance.patch(
        `${endpoints.accountants.updatePartialByTermStudent}/${termId}/${studentId}`,
        partialDto
      );
      toast.success(response.data.message || 'Accountant record updated successfully');
      if (onSuccess) {
        onSuccess();
      }
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating accountant record');
      console.error('Error updating accountant record:', error);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  // Get accountant record by ID
  const getAccountantData = async (accountantId) => {
    try {
      toggleLoading();
      const response = await axiosInstance.get(`${endpoints.accountants.get}${accountantId}`);
      return response.data.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching accountant record');
      console.error('Error fetching accountant record:', error);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  // Get accountant record by term and student
  const getAccountantDataByTermStudent = async (termId, studentId) => {
    try {
      const response = await axiosInstance.get(
        `${endpoints.accountants.getByTermStudent}/${termId}/${studentId}`
      );
      return response.data.data;
    } catch (error) {
      // 404 is expected when no record exists yet - don't show error or log to console
      if (error.response?.status === 404) {
        // Return null instead of throwing to indicate no record exists
        return null;
      }
      // Only show error for other status codes
      if (error.response?.status !== 404) {
        toast.error(error.response?.data?.message || 'Error fetching accountant record');
        console.error('Error fetching accountant record:', error);
      }
      throw error;
    }
  };

  // Get all accountant records for a student
  const getAccountantDataByStudent = async (studentId) => {
    try {
      toggleLoading();
      const response = await axiosInstance.get(`${endpoints.accountants.getByStudent}/${studentId}`);
      return response.data.data || [];
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching accountant records');
      console.error('Error fetching accountant records:', error);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  // Get all accountant records for a term
  const getAccountantDataByTerm = async (termId) => {
    try {
      toggleLoading();
      const response = await axiosInstance.get(`${endpoints.accountants.getByTerm}/${termId}`);
      return response.data.data || [];
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching accountant records');
      console.error('Error fetching accountant records:', error);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  // Get all distinct terms that have accountant records for a class
  const getAllTermsByClassId = async (classId) => {
    try {
      const response = await axiosInstance.get(`${endpoints.accountants.getTermsByClass}/${classId}`);
      return response.data.data || [];
    } catch (error) {
      // Don't show toast for 404 errors (no terms found)
      if (error.response?.status !== 404) {
        toast.error(error.response?.data?.message || 'Error fetching terms');
      }
      console.error('Error fetching terms by class ID:', error);
      return [];
    }
  };

  // Get all distinct terms that have accountant records for a student
  const getAllTermsByStudentId = async (studentId) => {
    try {
      const response = await axiosInstance.get(`${endpoints.accountants.getTermsByStudent}/${studentId}`);
      return response.data.data || [];
    } catch (error) {
      // Don't show toast for 404 errors (no terms found)
      if (error.response?.status !== 404) {
        toast.error(error.response?.data?.message || 'Error fetching terms');
      }
      console.error('Error fetching terms by student ID:', error);
      return [];
    }
  };

  // Get all accountant records for students in a class by term ID and class ID
  const getClassStudentsAccountantDataByTermIdAndClassId = async (termId, classId) => {
    try {
      toggleLoading();
      const response = await axiosInstance.get(
        `${endpoints.accountants.getByTermClass}/${termId}/${classId}`
      );
      return response.data.data || [];
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching accountant records');
      console.error('Error fetching accountant records by term and class:', error);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  // Check if accountant record exists
  const checkAccountantDataExists = async (termId, studentId) => {
    try {
      const response = await axiosInstance.get(
        `${endpoints.accountants.exists}/${termId}/${studentId}`
      );
      return response.data.data || false;
    } catch (error) {
      if (error.response?.status === 404) {
        return false;
      }
      console.error('Error checking accountant record existence:', error);
      return false;
    }
  };

  // Delete accountant record
  const deleteAccountantDataById = async (accountantId) => {
    try {
      toggleLoading();
      const response = await axiosInstance.delete(`${endpoints.accountants.delete}${accountantId}`);
      toast.success(response.data.message || 'Accountant record deleted successfully');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting accountant record');
      console.error('Error deleting accountant record:', error);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  // Send SMS/Email to parents about accounts data
  const sendSmsAboutAccountsDataToParents = async (studentId, accountData) => {
    try {
      const response = await axiosInstance.post(
        `${endpoints.accountants.sendSmsAboutAccountsData}/${studentId}`,
        accountData
      );
      toast.success(response.data.message || 'SMS/Email sent to parents successfully');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error sending SMS/Email to parents');
      console.error('Error sending SMS/Email to parents:', error);
      throw error;
    }
  };

  return {
    accountants,
    totalRecords,
    isLoading,
    createAccountantData,
    createAccountantDataByTermStudent,
    updateAccountantData,
    updateAccountantDataByTermStudent,
    updatePartialAccountantDataByTermStudent,
    getAccountantData,
    getAccountantDataByTermStudent,
    getAccountantDataByStudent,
    getAccountantDataByTerm,
    getAllTermsByClassId,
    getAllTermsByStudentId,
    getClassStudentsAccountantDataByTermIdAndClassId,
    checkAccountantDataExists,
    deleteAccountantDataById,
    sendSmsAboutAccountsDataToParents,
  };
};

export default useAccountantService;

